import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http'
import { inject } from '@angular/core'

import { catchError, switchMap, throwError } from 'rxjs'

import { AuthService } from '@yoshu-lib/core/auth/auth.service'
import { AuthStore } from '@yoshu-lib/core/auth/auth.store'

import { ENVIRONMENT } from '../tokens/environment.token'

/**
 * An HTTP interceptor function that adds an Authorization header with a Bearer token
 * to outgoing HTTP requests if a token is available in the AuthStore.
 *
 * @param req - The outgoing HTTP request to be intercepted.
 * @param next - A function to pass the request to the next handler in the chain.
 * @returns The modified HTTP request with the Authorization header if a token exists,
 *          or the original request if no token is available.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authStore = inject(AuthStore)
  const authService = inject(AuthService)
  const env = inject(ENVIRONMENT)

  const token = authStore.token()

  const isYoshuApiRequest = req.url.startsWith(env.apiUrl)
  const isLoginRequest = req.method === 'POST' && req.url.includes('/v1/auth/login')
  const isRefreshRequest = req.url.includes('/auth/refresh')

  if (!token || isLoginRequest || !isYoshuApiRequest || isRefreshRequest) return next(req)

  const authReq = token ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } }) : req

  return next(authReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401) {
        return authService.refreshAccessToken().pipe(
          switchMap((newAccessToken) => {
            const retriedReq = req.clone({
              setHeaders: { Authorization: `Bearer ${newAccessToken}` }
            })
            return next(retriedReq)
          }),
          catchError((refreshError) => {
            authService.logout().subscribe()
            return throwError(() => refreshError)
          })
        )
      }

      return throwError(() => error)
    })
  )
}
